import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ReCAPTCHA from 'react-google-recaptcha';
import classnames from 'classnames/bind';
import Icon from 'yoda-core-components/lib/components/Icon/Icon';
import GiftCardIllustration from 'yoda-core-components/lib/assets/svg/GiftCardIllustration.svg';
import Button from 'yoda-core-components/lib/components/Button';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import { giftCardLabels } from '../../../../common/LabelConstants';
import * as styles from './GiftCardForm.css';
import Pin from './UIComponents/Pin';
import Card from './UIComponents/Card';
import Constants from '../utility/Constants';
import { runValidator } from '../utility/giftCardValidation';

const cx = classnames.bind(styles);
export class GiftCardForm extends Component {
    static defaultProps = {
        giftCard: {
            giftCardNum: '',
            giftCardPin: '',
            captchaType: '',
            giftCardNumError: '',
            giftCardPinError: '',
            formLevelAPIError: '',
            giftCardBalances: [],
        },
    };

    static propTypes = {
        giftCard: PropTypes.objectOf(PropTypes.any),
        onSubmit: PropTypes.func.isRequired,
        onCaptchaChange: PropTypes.func.isRequired,
        onChange: PropTypes.func.isRequired,
        context: PropTypes.objectOf(PropTypes.any).isRequired,
        setGiftCardError: PropTypes.func.isRequired,
        triggerFormError: PropTypes.func.isRequired,
    };

    constructor() {
        super();
        this.onChange = this.handleChange.bind(this);
        this.onCaptchaChange = this.handleCaptchaChange.bind(this);
        this.onSubmit = this.handleSubmit.bind(this);
    }

    componentWillUnmount() {
        this.timer && clearTimeout(this.timer);
    }

    handleChange(event, eventProps) {
        if (
            event &&
            event.relatedTarget &&
            event.relatedTarget.id === 'checkGiftCardBalanceButton'
        ) {
            this.onSubmit(event);
        } else {
            this.props.onChange(event, eventProps);
        }
    }

    // Visible Captcha change
    /* istanbul ignore next */
    handleCaptchaChange(response) {
        this.props.onCaptchaChange(response);
        this.timer = setTimeout(() => {
            const button = document.getElementById('checkGiftCardBalanceButton');
            button.focus();
        }, 0);
    }

    handleSubmit(event) {
        const { enableV2GiftCardForm = false } = this.props.context.featureFlags || {};
        const errors = runValidator(
            {
                giftCardNum: this.props.giftCard.giftCardNum,
                giftCardPin: this.props.giftCard.giftCardPin,
            },
            { enableV2GiftCardForm }
        );
        if (!(errors.giftCardNumError === '' && errors.giftCardPinError === '')) {
            this.props.setGiftCardError(errors);
            // DA => Form error
            // eslint-disable-next-line no-unused-expressions
            errors.giftCardNumError === ''
                ? delete errors.giftCardNumError
                : errors.giftCardNumError;
            // eslint-disable-next-line no-unused-expressions
            errors.giftCardPinError === ''
                ? delete errors.giftCardPinError
                : errors.giftCardPinError;
            const formErrAttributes = Object.values(errors).map((e) => ({ errorDescription: e }));
            this.props.triggerFormError(formErrAttributes);
        } else if (this.props.giftCard.captchaType === Constants.INVISIBLE) {
            this.invisibleCaptcha.reset();
            this.invisibleCaptcha.execute();
        } else {
            this.visibleCaptcha.reset();
            this.props.onSubmit(event);
        }
    }

    // Render Captcha based on feature flag and cookie
    renderCaptcha() {
        if (this.props.giftCard.captchaType === Constants.INVISIBLE) {
            return (
                <ReCAPTCHA
                    sitekey={this.props.context.preferences.gcInVisibleCaptchaKey}
                    size="invisible"
                    badge="bottomleft"
                    onChange={this.onCaptchaChange}
                    ref={(e) => {
                        this.invisibleCaptcha = e;
                    }}
                />
            );
        }
        return (
            <span data-automation-id="at-visible-captcha">
                <ReCAPTCHA
                    sitekey={this.props.context.preferences.gcVisibleCaptchakey}
                    onChange={this.onCaptchaChange}
                    ref={(e) => {
                        this.visibleCaptcha = e;
                    }}
                />
            </span>
        );
    }

    renderCard() {
        const { enableV2GiftCardForm = false } = this.props.context.featureFlags || {};
        return (
            <div className={` ${dt(['lg:w-[260px]', 'lg:mr-6', 'smMd:w-full', 'smMd:mr-0'])}`}>
                <Card
                    onChange={this.onChange}
                    value={this.props.giftCard.giftCardNum}
                    error={this.props.giftCard.giftCardNumError}
                    deviceType={this.props.context.deviceType}
                    enableV2GiftCardForm={enableV2GiftCardForm}
                />
            </div>
        );
    }

    renderPin() {
        const { enableV2GiftCardForm = false } = this.props.context.featureFlags || {};
        return (
            <div className={dt(['mx-auto', 'lg:w-[calc(100%-284px)]', 'smMd:w-full'])}>
                <Pin
                    onChange={this.onChange}
                    value={this.props.giftCard.giftCardPin}
                    error={this.props.giftCard.giftCardPinError}
                    enableV2GiftCardForm={enableV2GiftCardForm}
                />
            </div>
        );
    }

    renderFields() {
        return (
            <div className={`${cx('inputWrapper')} ${dt(['flex', 'smMd:flex-col'])}`}>
                {this.renderCard()}
                {this.renderPin()}
            </div>
        );
    }

    render() {
        return (
            <form onSubmit={this.onSubmit}>
                <div
                    className={`${cx('giftCardBalanceHeading')} ${dt([
                        'sm:text-xlarge',
                        'pb-6',
                        'text-black',
                        'text-center',
                        'font-open-sans-semibold',
                    ])}`}
                >
                    {giftCardLabels.GIFT_CARD_PAGE_TITLE}
                </div>
                {this.renderFields()}
                <div
                    className={`${cx('imageCard')} ${dt([
                        'sm:mt-0',
                        'sm:mx-auto',
                        'sm:mb-6',
                        'flex',
                        'justify-center',
                    ])}`}
                >
                    <Icon iconType="svg" width="327px" height="138px" viewBox="0 0 327 138">
                        <GiftCardIllustration />
                    </Icon>
                </div>
                <div className={`${cx('renderCaptcha')} ${dt(['my-0', 'mx-auto'])}`}>
                    {!__SERVER__ && this.renderCaptcha()}
                </div>
                <Button
                    automationId="at-giftcard-check-balance-btn"
                    buttonId="checkGiftCardBalanceButton"
                    size="Lg"
                    buttonType="Primary"
                    className={`${cx('buttonCheckBalance')} ${dt([
                        'sm:w-full',
                        'mt-6',
                        'lg:max-w-[300px]',
                        'mx-auto',
                        'block',
                    ])}`}
                    onClick={() => this.onSubmit()}
                >
                    {this.props.giftCard.giftCardBalances.length > 0
                        ? Constants.CHECK_ANOTHER_CARD
                        : Constants.CHECK_BALANCE}
                </Button>
            </form>
        );
    }
}

export default GiftCardForm;
